import React from 'react'
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    ComposedChart,
    Cell,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    LabelList,
} from 'recharts'

/**
 * Recharts replacement for the four dashboard charts. Preserves the same
 * input data shapes (charts.casesOverTime / .bookingsOverTime /
 * .inquiryConversion / .topMediators) and the same click-to-navigate +
 * hover-tooltip behaviour the chart.js versions had.
 */

const SHORT_MONTH_TO_FULL = {
    Jan: 'January', Feb: 'February', Mar: 'March', Apr: 'April', May: 'May', Jun: 'June',
    Jul: 'July', Aug: 'August', Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December',
}
const SHORT_MONTH_TO_IDX = {
    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
}

function monthBoundaryRange(label, year) {
    const m = SHORT_MONTH_TO_IDX[label]
    if (m == null || !year) return { startDate: '', endDate: '' }
    const start = new Date(Date.UTC(year, m, 1))
    const endLastDay = new Date(Date.UTC(year, m + 1, 0))
    const fmt = (d) => d.toISOString().slice(0, 10)
    return { startDate: fmt(start), endDate: fmt(endLastDay) }
}

function bucketDateRange(bucket) {
    if (!bucket) return { startDate: '', endDate: '' }
    if (bucket.startIso && bucket.endIso) {
        return {
            startDate: bucket.startIso.slice(0, 10),
            endDate: new Date(new Date(bucket.endIso).getTime() - 86400000).toISOString().slice(0, 10),
        }
    }
    return monthBoundaryRange(bucket.label, bucket.year)
}

const tickFont = { fontFamily: 'Inter Tight', fontSize: 13, fill: '#0e1e20' }

function CardTooltip({ children }) {
    return (
        <div
            className="bg-white border border-gray-200 rounded-lg shadow-sm p-3"
            style={{ fontFamily: 'Inter Tight', fontSize: 11 }}
        >
            {children}
        </div>
    )
}

/* ────────────────────────── Cases Over Time ────────────────────────── */

function CasesOverTimeTooltip({ active, payload, label, casesOverTime }) {
    if (!active || !payload || !payload.length) return null
    const idx = payload[0].payload.__idx
    const buckets = casesOverTime?.buckets
    const counts = casesOverTime?.counts || []
    const labels = casesOverTime?.labels || []
    const monthFull = buckets?.[idx]?.label
        ? `${SHORT_MONTH_TO_FULL[buckets[idx].label] || buckets[idx].label} ${buckets[idx].year}`
        : `${SHORT_MONTH_TO_FULL[label] || label} ${new Date().getFullYear()}`
    const cur = counts[idx] ?? 0
    const prev = idx > 0 ? counts[idx - 1] ?? 0 : null
    const diff = prev != null ? cur - prev : null
    const sign = diff != null && diff > 0 ? '+' : ''
    const prevLabel = idx > 0 ? labels[idx - 1] : ''
    return (
        <CardTooltip>
            <div className="font-semibold text-[#0e1e20] mb-1">{monthFull}</div>
            <div className="text-[#0e1e20]">Total Cases: {cur}</div>
            {prevLabel ? (
                <div className="mt-1" style={{ color: '#4087fc', fontWeight: 500 }}>
                    {sign}{diff} vs {prevLabel}
                </div>
            ) : null}
        </CardTooltip>
    )
}

export function CasesOverTimeChart({ casesOverTime, navigate, height = 192 }) {
    const labels = casesOverTime?.labels ?? []
    const counts = casesOverTime?.counts ?? []
    const data = labels.map((l, i) => ({ label: l, count: counts[i] ?? 0, __idx: i }))
    if (data.length === 0) {
        return <div style={{ height, width: '100%' }} className="flex items-center justify-center text-gray-400 text-sm">No data</div>
    }

    // BarChart-level onClick uses activeTooltipIndex (hover-then-click).
    // Direct clicks on areas/bars also fire Bar/Area onClick which gives the
    // data point directly — wired below for reliability.
    const navigateForIdx = (idx) => {
        if (!navigate || idx == null) return
        const bucket = casesOverTime?.buckets?.[idx]
        if (!bucket) return
        const { startDate, endDate } = bucketDateRange(bucket)
        if (startDate && endDate) navigate(`/cases?startDate=${startDate}&endDate=${endDate}&dateField=mediation_date`)
    }
    const handleClick = (state) => {
        if (state && state.activeTooltipIndex != null) navigateForIdx(state.activeTooltipIndex)
    }

    return (
        <div style={{ height, width: '100%', cursor: 'pointer' }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} onClick={handleClick} margin={{ top: 12, bottom: 8, left: 0, right: 8 }}>
                    <CartesianGrid stroke="#e5e7eb" strokeDasharray="4 4" />
                    <XAxis dataKey="label" axisLine={{ stroke: '#d1d5db' }} tickLine={false} tick={tickFont} interval="preserveStartEnd" minTickGap={24} />
                    <YAxis
                        axisLine={{ stroke: '#d1d5db' }}
                        tickLine={false}
                        tick={tickFont}
                        allowDecimals={false}
                    />
                    <Tooltip
                        cursor={{ stroke: '#9ca3af', strokeDasharray: '4 4' }}
                        content={<CasesOverTimeTooltip casesOverTime={casesOverTime} />}
                    />
                    <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#499e99"
                        strokeWidth={2}
                        dot={{ r: 4, stroke: '#499e99', strokeWidth: 2, fill: '#fff' }}
                        activeDot={{
                            r: 5, stroke: '#499e99', strokeWidth: 2, fill: '#fff',
                            onClick: (_e, p) => navigateForIdx(p?.payload?.__idx),
                            style: { cursor: 'pointer' },
                        }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    )
}

/* ─────────────────────── Bookings Over Time ─────────────────────── */

function BookingsOverTimeTooltip({ active, payload, label, bookingsOverTime }) {
    if (!active || !payload || !payload.length) return null
    const idx = payload[0].payload.__idx
    const buckets = bookingsOverTime?.buckets
    const counts = bookingsOverTime?.counts || []
    const labels = bookingsOverTime?.labels || []
    const monthFull = buckets?.[idx]?.label
        ? `${SHORT_MONTH_TO_FULL[buckets[idx].label] || buckets[idx].label} ${buckets[idx].year}`
        : `${SHORT_MONTH_TO_FULL[label] || label} ${new Date().getFullYear()}`
    const cur = counts[idx] ?? 0
    const prev = idx > 0 ? counts[idx - 1] ?? 0 : null
    const diff = prev != null ? cur - prev : null
    const sign = diff != null && diff > 0 ? '+' : ''
    const prevLabel = idx > 0 ? labels[idx - 1] : ''
    return (
        <CardTooltip>
            <div className="font-semibold text-[#0e1e20] mb-1">{monthFull}</div>
            <div className="text-[#0e1e20]">Total Bookings: {cur}</div>
            {prevLabel ? (
                <div className="mt-1" style={{ color: '#4087fc', fontWeight: 500 }}>
                    {sign}{diff} vs {prevLabel}
                </div>
            ) : null}
        </CardTooltip>
    )
}

export function BookingsOverTimeChart({ bookingsOverTime, navigate, height = 192 }) {
    const labels = bookingsOverTime?.labels ?? []
    const counts = bookingsOverTime?.counts ?? []
    const data = labels.map((l, i) => ({ label: l, count: counts[i] ?? 0, __idx: i }))
    if (data.length === 0) {
        return <div style={{ height, width: '100%' }} className="flex items-center justify-center text-gray-400 text-sm">No data</div>
    }

    const navigateForIdx = (idx) => {
        if (!navigate || idx == null) return
        const bucket = bookingsOverTime?.buckets?.[idx]
        if (!bucket) return
        const { startDate, endDate } = bucketDateRange(bucket)
        if (startDate && endDate) navigate(`/cases?startDate=${startDate}&endDate=${endDate}&dateField=created_at`)
    }
    const handleClick = (state) => {
        if (state && state.activeTooltipIndex != null) navigateForIdx(state.activeTooltipIndex)
    }

    return (
        <div style={{ height, width: '100%', cursor: 'pointer' }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} onClick={handleClick} margin={{ top: 12, bottom: 8, left: 0, right: 8 }}>
                    <CartesianGrid stroke="#e5e7eb" strokeDasharray="4 4" />
                    <XAxis dataKey="label" axisLine={{ stroke: '#d1d5db' }} tickLine={false} tick={tickFont} interval="preserveStartEnd" minTickGap={24} />
                    <YAxis axisLine={{ stroke: '#d1d5db' }} tickLine={false} tick={tickFont} allowDecimals={false} />
                    <Tooltip
                        cursor={{ stroke: '#9ca3af', strokeDasharray: '4 4' }}
                        content={<BookingsOverTimeTooltip bookingsOverTime={bookingsOverTime} />}
                    />
                    <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#4087fc"
                        strokeWidth={2}
                        dot={{ r: 4, stroke: '#4087fc', strokeWidth: 2, fill: '#fff' }}
                        activeDot={{
                            r: 5, stroke: '#4087fc', strokeWidth: 2, fill: '#fff',
                            onClick: (_e, p) => navigateForIdx(p?.payload?.__idx),
                            style: { cursor: 'pointer' },
                        }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    )
}

/* ────────────────────── Inquiry Conversion ────────────────────── */

function InquiryConversionTooltip({ active, payload, label, inquiryConversion }) {
    if (!active || !payload || !payload.length) return null
    const idx = payload[0].payload.__idx
    const total = inquiryConversion?.totals?.[idx] ?? 0
    const convPct = inquiryConversion?.converted?.[idx] ?? 0
    const convertedCount = Math.round((total * convPct) / 100)
    const notConvertedCount = Math.max(0, total - convertedCount)
    // Suppress tooltip on empty months — no inquiries means no useful info.
    if (total <= 0) return null
    return (
        <CardTooltip>
            <div className="font-semibold text-[#0e1e20] text-[13px] mb-1">{label}</div>
            <div className="text-[#0e1e20] text-[12px] mb-2">Total inquiries: {total}</div>
            <div className="space-y-1 text-[12px] text-[#0e1e20]">
                <div>Conversion rate: {convPct}%</div>
                <div>Not Converted: {notConvertedCount}</div>
                <div>Converted: {convertedCount}</div>
            </div>
        </CardTooltip>
    )
}

export function InquiryConversionChart({ inquiryConversion, navigate, height = 192 }) {
    const fallbackLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const labels = inquiryConversion?.labels ?? fallbackLabels
    const totals = inquiryConversion?.totals ?? Array(labels.length).fill(0)
    const convertedPct = inquiryConversion?.converted ?? Array(labels.length).fill(0)
    // Single bar (one color) per month showing total inquiries. The
    // conversion-rate line is continuous — empty months get 0% so the line
    // dips back to the x-axis rather than breaking. Dots still render only
    // on months that have at least one inquiry (custom dot renderer below).
    const data = labels.map((l, i) => {
        const total = totals[i] ?? 0
        const pct = convertedPct[i] ?? 0
        const conv = Math.round((total * pct) / 100)
        const notConv = Math.max(0, total - conv)
        return {
            label: l,
            total,
            converted: conv,
            notConverted: notConv,
            conversionRate: total > 0 ? pct : 0,
            __idx: i,
        }
    })
    // Append a synthetic trailing point at 0% so the line peaks at the
    // last real data point and dips back down to the x-axis on the right
    // edge — gives the line a clear "wave" shape instead of ending mid-air
    // at the last data peak. The synthetic point has total=0 so no bar
    // renders, no dot, and we hide its X-axis label below.
    if (data.length > 0) {
        data.push({
            label: '__trail',
            total: 0,
            converted: 0,
            notConverted: 0,
            conversionRate: 0,
            __idx: -1,
            __trailing: true,
        })
    }
    if (data.length === 0 || data.every((d) => !d.converted && !d.notConverted)) {
        // Empty state — keep the chart frame visible by rendering "No data" centered.
        // For a fully-empty range we render the message; for a range with zero
        // inquiries we still let recharts render the empty axes so the user
        // sees the bucket labels.
        if (data.length === 0) {
            return <div style={{ height, width: '100%' }} className="flex items-center justify-center text-gray-400 text-sm">No data</div>
        }
    }

    const navigateForIdx = (idx) => {
        if (!navigate || idx == null) return
        const bucket = inquiryConversion?.buckets?.[idx]
        if (!bucket) return
        const { startDate, endDate } = bucketDateRange(bucket)
        if (startDate && endDate) navigate(`/inquiries?startDate=${startDate}&endDate=${endDate}`)
    }
    const handleClick = (state) => {
        if (state && state.activeTooltipIndex != null) navigateForIdx(state.activeTooltipIndex)
    }
    const handleBarClick = (d) => navigateForIdx(d?.__idx)

    return (
        <div style={{ height, width: '100%', cursor: 'pointer' }}>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} onClick={handleClick} margin={{ top: 12, right: 8, bottom: 8, left: 0 }} barCategoryGap="30%">
                    {/* Faint dashed grid (matches Cases Over Time / Bookings
                        Over Time) so the conversion-rate dots are visually
                        anchored to the percentage ticks on the right axis —
                        easy to tell at a glance whether a dot is at 50% / 60%
                        / etc., even when the bar below it is empty. */}
                    <CartesianGrid stroke="#e5e7eb" strokeDasharray="4 4" yAxisId="right" />
                    <XAxis
                        dataKey="label"
                        axisLine={{ stroke: '#d1d5db' }}
                        tickLine={false}
                        // Auto-thin labels: every bucket still renders a bar, but
                        // the x-axis only labels as many dates as fit without
                        // overlapping (keeps first + last). interval={0} forced all
                        // 30 daily labels to draw → they collided. minTickGap keeps
                        // ~24px between labels so a 30-day range stays readable.
                        interval="preserveStartEnd"
                        minTickGap={24}
                        tick={(props) => {
                            const { x, y, payload } = props
                            // Hide the synthetic trailing tick (label === '__trail').
                            if (payload?.value === '__trail') return null
                            return (
                                <text
                                    x={x}
                                    y={y + 8}
                                    textAnchor="middle"
                                    fill="#0e1e20"
                                    fontFamily="Inter Tight"
                                    fontSize={10}
                                    fontWeight={500}
                                >
                                    {payload?.value}
                                </text>
                            )
                        }}
                    />
                    <YAxis
                        yAxisId="left"
                        axisLine={{ stroke: '#d1d5db' }}
                        tickLine={false}
                        tick={tickFont}
                        allowDecimals={false}
                        // Match the axis to the actual peak (not recharts' auto-
                        // padded ceiling). With totals of 2/2 the axis now tops
                        // out at 2 instead of recharts' default 4.
                        domain={[0, (dataMax) => Math.max(1, Math.ceil(dataMax))]}
                        label={{ value: 'Inquiries', angle: -90, position: 'insideLeft', style: { fill: '#0e1e20', fontFamily: 'Inter Tight', fontSize: 10, fontWeight: 500 } }}
                    />
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        axisLine={{ stroke: '#d1d5db' }}
                        tickLine={false}
                        domain={[0, 100]}
                        ticks={[0, 20, 40, 60, 80, 100]}
                        tick={tickFont}
                        tickFormatter={(v) => `${v}%`}
                        label={{ value: 'Conversion rate', angle: 90, position: 'insideRight', style: { fill: '#0e1e20', fontFamily: 'Inter Tight', fontSize: 10, fontWeight: 500 } }}
                    />
                    <Tooltip
                        cursor={{ fill: 'rgba(17,94,89,0.04)' }}
                        content={<InquiryConversionTooltip inquiryConversion={inquiryConversion} />}
                    />
                    {/* Single bar per month — one color (dark teal),
                        height = total inquiries. */}
                    <Bar
                        yAxisId="left"
                        dataKey="total"
                        fill="#115e59"
                        maxBarSize={28}
                        // Round only the top of each bar; bottom stays flat
                        // against the x-axis.
                        radius={[4, 4, 0, 0]}
                        onClick={handleBarClick}
                        style={{ cursor: 'pointer' }}
                    />
                    {/* Continuous conversion-rate line — dips to 0% on empty
                        months so the line stays connected across the whole
                        range (no breaks). Dots ONLY render on months that
                        actually had inquiries (total > 0). */}
                    <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="conversionRate"
                        stroke="#115e59"
                        strokeWidth={2}
                        dot={(props) => {
                            const { cx, cy, payload, index } = props
                            // Recharts treats `null` from a dot renderer as
                            // "use default" — return an empty <g/> instead so
                            // empty months produce no visible dot.
                            const empty = <g key={`inq-dot-empty-${index}`} />
                            if (cx == null || cy == null || isNaN(cx) || isNaN(cy)) return empty
                            if (!payload || Number(payload.total ?? 0) <= 0) return empty
                            return (
                                <circle
                                    key={`inq-dot-${index}`}
                                    cx={cx}
                                    cy={cy}
                                    r={4}
                                    stroke="#115e59"
                                    strokeWidth={2}
                                    fill="#fff"
                                />
                            )
                        }}
                        activeDot={(props) => {
                            const { cx, cy, payload, index } = props
                            const empty = <g key={`inq-act-empty-${index}`} />
                            if (cx == null || cy == null || isNaN(cx) || isNaN(cy)) return empty
                            if (!payload || Number(payload.total ?? 0) <= 0) return empty
                            return (
                                <circle
                                    key={`inq-act-${index}`}
                                    cx={cx}
                                    cy={cy}
                                    r={5}
                                    stroke="#115e59"
                                    strokeWidth={2}
                                    fill="#fff"
                                />
                            )
                        }}
                        isAnimationActive={false}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    )
}

/* ────────────────────── Top Mediators ────────────────────── */

const TOP_MEDIATOR_PALETTE = [
    '#115e59', '#55a29d', '#8ac9c5', '#d7edeb', '#ebf5f4',
    '#f4f5f6', '#f3f4f6', '#ebf5f4', '#d7edeb', '#8ac9c5',
]

// Custom value-pill renderer drawn above each bar (mirrors chart.js
// barValuePlugin output: white pill with #e5e5e5 border, count text).
function ValuePill(props) {
    const { x, y, width, value } = props
    if (value == null) return null
    const text = String(value)
    const padX = 8
    const charW = 6 // approx for Inter Tight 10px
    const w = Math.max(20, text.length * charW + padX * 2)
    const h = 16
    const cx = x + width / 2
    const cy = (y || 0) - 14
    return (
        <g pointerEvents="none">
            <rect
                x={cx - w / 2}
                y={cy - h / 2}
                width={w}
                height={h}
                rx={6}
                ry={6}
                fill="#ffffff"
                stroke="#e5e5e5"
                strokeWidth={1}
            />
            <text
                x={cx}
                y={cy}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ fontFamily: 'Inter Tight', fontWeight: 500, fontSize: 10, fill: '#0e1e20' }}
            >
                {text}
            </text>
        </g>
    )
}

export function TopMediatorsChart({ topMediators, navigate, height = 192 }) {
    const names = topMediators?.names ?? []
    const counts = topMediators?.counts ?? []
    const percentages = topMediators?.percentages ?? []
    const ids = topMediators?.ids ?? []
    const data = names.map((n, i) => ({
        label: n,
        value: percentages[i] ?? 0,
        count: counts[i] ?? 0,
        id: ids[i],
        __idx: i,
    }))

    const navigateForIdx = (idx) => {
        if (!navigate || idx == null) return
        const id = ids[idx]
        if (id) navigate(`/mediators/${id}`)
    }
    const handleClick = (state) => {
        if (state && state.activeTooltipIndex != null) navigateForIdx(state.activeTooltipIndex)
    }
    const handleBarClick = (data) => navigateForIdx(data?.__idx)

    // Empty state — no mediator activity in the selected period.
    if (!data || data.length === 0) {
        return (
            <div
                style={{ height, width: '100%' }}
                className="flex items-center justify-center text-gray-400 text-sm"
            >
                No data
            </div>
        )
    }

    return (
        <div style={{ height, width: '100%', cursor: 'pointer' }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} onClick={handleClick} margin={{ top: 24, right: 8, bottom: 8, left: 0 }}>
                    <XAxis dataKey="label" axisLine={{ stroke: '#d1d5db' }} tickLine={false} tick={tickFont} interval={0} />
                    <YAxis axisLine={{ stroke: '#d1d5db' }} tickLine={false} tick={tickFont} hide={false} width={1} />
                    <Bar
                        dataKey="value"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={56}
                        onClick={handleBarClick}
                        style={{ cursor: 'pointer' }}
                    >
                        {data.map((d, i) => (
                            <Cell key={i} fill={TOP_MEDIATOR_PALETTE[i % TOP_MEDIATOR_PALETTE.length]} />
                        ))}
                        <LabelList dataKey="count" content={<ValuePill />} />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    )
}
